import { loadIntlWithLocale } from 'nucleus-text/intlLoaders/index';
import getMatchingLocale from 'nucleus-text/util/getMatchingLocale';
import locales from '../../appInitialization/locales';
import { setLocale } from 'nucleus-widgets/redux/modules/text';
import { saveLocaleId } from '../registrationForm/regCart/actions';
import { getFullLanguageFromDefaultMapping } from 'event-widgets/utils/getMatchingLocale';
import { loadLocalizedUserText } from 'nucleus-guestside-site/src/redux/modules/localizedUserText';
import { registerTranslationsFromSnapshot, convertLocaleToCorrectCasing, getEventDefaultCultureCode,
  getEventLocales, getCurrentLocale } from 'event-widgets/utils/multiLanguageUtils';
import { loadLanguageManagementSettings } from '../actions';
import { createLoadCountry } from 'event-widgets/redux/modules/country';
import { registerTranslation } from 'nucleus-guestside-site/src/redux/modules/text';
import { loadMultiStates } from 'event-widgets/redux/modules/state';

export const LOAD_LANGUAGE = 'event-guestside-site/mutliLanguage/LOAD_LANGUAGE';

export const loadLanguage = (locale) => {
  return async (dispatch, getState) => {
    const {
      text,
      multiLanguageLocale,
      clients: { userTextClient, translationSnapshotClient },
      event,
      event: { cultureCode, eventLocalesSetup = {} } = {},
      localizedUserText = {},
      experiments: { allowMultiLanguage = false } = {},
      event: { eventLocalesSetup: { eventLocales = [] } = {} },
      account: { settings: { defaultCultureCode = cultureCode } },
      registrationForm
    } = getState();
    if (eventLocales.length > 1) {
      const caseCorrectedLocale = convertLocaleToCorrectCasing(locale);
      const currentLocale = getCurrentLocale(caseCorrectedLocale, eventLocales);
      const setAndSaveLocale = () => {
        dispatch(saveLocaleId(getLocaleIdFromCultureCode(caseCorrectedLocale, eventLocales)));
        dispatch(setLocale(caseCorrectedLocale));
      };
      const availableLocales = getEventLocales(event);
      if (availableLocales.includes(locale.toLowerCase())
      || locale === cultureCode || availableLocales.includes(currentLocale.toLowerCase())) {
        const promises = [];
        if (!multiLanguageLocale.loadedLanguages.includes(caseCorrectedLocale)
          || !multiLanguageLocale.loadedLanguages.includes(currentLocale)) {
          promises.push(registerTranslationsFromSnapshot(text.resolver, translationSnapshotClient,
            getEventDefaultCultureCode(event), defaultCultureCode, caseCorrectedLocale));
          promises.push(loadBaseTranslations(caseCorrectedLocale, text));

          if (allowMultiLanguage) {
            promises.push(loadUserTextTranslations(localizedUserText, caseCorrectedLocale, eventLocalesSetup,
              userTextClient, dispatch));
          }
          promises.push(dispatch(loadLanguageManagementSettings()));
          promises.push(dispatch(createLoadCountry(registerTranslation,
            { locale: caseCorrectedLocale, translate: text.translate })));
          promises.push(dispatch({
            type: LOAD_LANGUAGE,
            payload: caseCorrectedLocale
          }));
        }
        const currentlySelectedCountries = getSelectedCountries(registrationForm);
        if (currentlySelectedCountries.length > 0) {
          promises.push(dispatch(loadMultiStates(registerTranslation, currentlySelectedCountries,
            caseCorrectedLocale)));
        }
        await Promise.all(promises);
        setAndSaveLocale();
      }
    }
  };
};

export function getLocaleIdFromCultureCode(cultureCode, eventLocales) {
  if (eventLocales && Array.isArray(eventLocales)) {
    const fullLocale = getFullLanguageFromDefaultMapping(cultureCode);
    const matchingLocale = eventLocales.find(eventLocale => eventLocale.cultureCode === fullLocale);
    return matchingLocale ? matchingLocale.localeId : null;
  }
  return null;
}

export function getCultureCodeFromLocaleId(localeId, eventLocales) {
  if (eventLocales && Array.isArray(eventLocales)) {
    const matchingLocale = eventLocales.find(locale => locale.localeId === localeId);
    return matchingLocale ? matchingLocale.cultureCode : null;
  }
  return null;
}

/**
 * Gets UserText for locale
 */
async function getUserText(localizedUserText, locale, eventLocalesSetup, userTextClient) {
  let userText = localizedUserText.localization
    ? localizedUserText.localization[locale] || {}
    : {};
  if (eventLocalesSetup.eventLocales) {
    const defaultLocale = eventLocalesSetup.eventLocales.find(l => l.defaultFlag);
    const eventLocale = eventLocalesSetup.eventLocales.find(l => l.cultureCode === locale);
    if (eventLocale && defaultLocale && defaultLocale.localeId === eventLocale.localeId) {
      userText = {};
    } else if (eventLocale) {
      const localizedUserTextForLocale = await userTextClient.getUserTextForLocale(eventLocale.localeId);
      if (localizedUserTextForLocale) {
        userText = Object.keys(localizedUserTextForLocale.data)
          .reduce((result, key) => {
            if (localizedUserTextForLocale.data[key].richText) {
              /**
               * if user text has rich text format, render rich text.
               */
              return { ...result,
                [key]: JSON.parse(localizedUserTextForLocale.data[key].richText)
              };
            }
            return { ...result,
              [key]: localizedUserTextForLocale.data[key].text
            };
          }, {});
      }
    }
  }
  return userText;
}

function getSelectedCountries(registrationForm) {
  const eventRegistrations = registrationForm?.regCart?.eventRegistrations;
  let countryCodes = [];
  if (eventRegistrations) {
    Object.keys(eventRegistrations).forEach(key => {
      const homeAddressCountryCode = eventRegistrations[key].attendee?.personalInformation?.homeAddress?.countryCode;
      const workAddressCountryCode = eventRegistrations[key].attendee?.personalInformation?.workAddress?.countryCode;
      if (homeAddressCountryCode && homeAddressCountryCode !== '' && !countryCodes.includes(homeAddressCountryCode)) {
        countryCodes.push(homeAddressCountryCode);
      }
      if (workAddressCountryCode && workAddressCountryCode !== '' && !countryCodes.includes(workAddressCountryCode)) {
        countryCodes.push(workAddressCountryCode);
      }
    });
  }
  const paymentCountryCode = registrationForm?.regCartPayment?.pricingInfo?.creditCard?.country;
  if (paymentCountryCode && paymentCountryCode !== '' && !countryCodes.includes(paymentCountryCode)) {
    countryCodes.push(paymentCountryCode);
  }
  return countryCodes;
}

async function loadBaseTranslations(locale, text) {
  const translationsPromise = new Promise(getMatchingLocale(locale, locales));
  await new Promise(resolve => loadIntlWithLocale(locale, resolve));
  text.resolver.registerTranslations(await translationsPromise, 'default', locale);
}

async function loadUserTextTranslations(localizedUserText, locale, eventLocalesSetup, userTextClient, dispatch) {
  const userText = await getUserText(localizedUserText, locale, eventLocalesSetup,
    userTextClient);
  dispatch(loadLocalizedUserText(locale, userText));
}
